import { useState, useEffect, useRef } from 'react';
import type { CapabilityInstance } from 'homey-api';

import { ToastManager } from '../../ToastManager.jsx';

import { useDevice } from './useDevices.ts';
import { useEffectEvent } from '../../hooks/useEffectEvent.ts';

export function useCapabilityValue({
  deviceId,
  capabilityId,
}: {
  deviceId: string;
  capabilityId?: string;
}) {
  const pendingRef = useRef<Promise<void> | undefined>(undefined);
  const capabilityInstanceRef = useRef<CapabilityInstance | null>(null);

  const { deviceInstance, device } = useDevice({ deviceId: deviceId });

  const capabilityObj =
    capabilityId != null ? device?.capabilitiesObj?.[capabilityId] ?? null : null;

  const [value, setValue] = useState(capabilityObj?.value ?? null);

  useEffect(() => {
    setValue(capabilityObj?.value ?? null);
  }, [capabilityObj]);

  useEffect(() => {
    if (capabilityObj?.id == null || deviceInstance == null) {
      return;
    }

    // TODO
    // connect the item so we have a list
    const capabilityInstance = deviceInstance?.makeCapabilityInstance(capabilityObj.id, (value) => {
      setValue(value);
    });

    capabilityInstanceRef.current = capabilityInstance;

    return function () {
      capabilityInstance?.destroy();
    };
  }, [deviceInstance, capabilityObj?.id]);

  type SetValueOptions = {
    local?: boolean;
  };

  const _setValue = useEffectEvent(
    (nextValue: null | number | string | boolean, options?: SetValueOptions) => {
      if (capabilityObj == null) {
        return Promise.reject(new Error('Capability object is not available'));
      }

      const isGetable = capabilityObj.getable === true;

      if (capabilityObj.setable === false) {
        return Promise.reject(new Error(`Capability ${capabilityObj.id} is not setable`));
      }

      const prevValue = value;

      if (isGetable === true) {
        setValue(nextValue);
      }

      if (options?.local === true) {
        return Promise.resolve();
      }

      const promise: Promise<void> =
        capabilityInstanceRef.current?.setValue(nextValue) ??
        Promise.reject(new Error('Capability instance not available'));

      pendingRef.current = promise;

      promise
        .then((result) => {})
        .catch((error) => {
          ToastManager.handleError(error);
          if (pendingRef.current === promise && isGetable === true) {
            setValue(prevValue);
          }
        })
        .finally(() => {
          if (pendingRef.current === promise) {
            pendingRef.current = undefined;
          }
        });

      return promise;
    }
  );

  return [value, _setValue, capabilityObj] as const;
}
